import React, { useRef, useEffect, useCallback, forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './SimpleEditor.css';

const SimpleEditor = forwardRef(({ value, onChange, placeholder, rows = 3, showToolbar = true, ...props }, ref) => {
  const { t } = useTranslation();
  const editorRef = useRef(null);

  // Function to process HTML with Tailwind classes
  const processHtmlWithTailwind = useCallback((html) => {
    if (!html) return html;
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Process all elements
    const allElements = tempDiv.querySelectorAll('*');
    
    allElements.forEach(element => {
      const style = element.getAttribute('style') || '';
      let className = element.getAttribute('class') || '';
      
      // Replace font-size with Tailwind class
      let newStyle = style.replace(/font-size:\s*\d+px;?\s*/g, '').trim();
      
      // Clear all existing font-size classes before adding new ones
      className = className.replace(/\btext-(xs|sm|base|lg|xl|2xl|3xl|4xl|5xl|6xl)\b/g, '').trim();
      
      // Add responsive Tailwind classes for font-size
      const fontSizeClass = 'text-base md:text-xl';
      className = className ? `${className} ${fontSizeClass}` : fontSizeClass;
      
      // Special handling for image containers
      if (element.hasAttribute && element.hasAttribute('data-image-group')) {
        const groupType = element.getAttribute('data-image-group');
        
        // AGGRESSIVELY clear ALL layout classes before adding new ones
        let containerClassName = className
          .replace(/\b(grid|flex|grid-cols-\d+|flex-wrap|gap-\d+|my-\d+|justify-center|justify-start|justify-end|text-left|text-center|text-right|w-full)\b/g, '')
          .trim();
        
        // Clear ALL inline styles related to layout
        newStyle = newStyle
          .replace(/display:\s*(grid|flex);?\s*/g, '')
          .replace(/grid-template-columns:\s*[^;]+;?\s*/g, '')
          .replace(/flex-wrap:\s*wrap;?\s*/g, '')
          .replace(/gap:\s*\d+px;?\s*/g, '')
          .replace(/margin:\s*\d+px\s*0;?\s*/g, '')
          .replace(/text-align:\s*(left|center|right);?\s*/g, '')
          .replace(/justify-content:\s*(center|flex-start|flex-end);?\s*/g, '')
          .replace(/width:\s*100%;?\s*/g, '')
          .replace(/max-width:\s*100%;?\s*/g, '')
          .trim();
        
        if (groupType === 'column') {
          // For column - always 100% width, alignment doesn't apply
          containerClassName = containerClassName ? `${containerClassName} grid grid-cols-1 gap-2 my-2 w-full` : 'grid grid-cols-1 gap-2 my-2 w-full';
        } else if (groupType === 'row') {
          // For row - always 2 images, alignment can be applied
          containerClassName = containerClassName ? `${containerClassName} flex flex-wrap gap-2 my-2` : 'flex flex-wrap gap-2 my-2';
          
          // Apply alignment only for rows
          if (style.includes('text-align: center')) {
            containerClassName = containerClassName ? `${containerClassName} justify-center` : 'justify-center';
          } else if (style.includes('text-align: left')) {
            containerClassName = containerClassName ? `${containerClassName} justify-start` : 'justify-start';
          } else if (style.includes('text-align: right')) {
            containerClassName = containerClassName ? `${containerClassName} justify-end` : 'justify-end';
          }
        }
        
        className = containerClassName;
      } else {
        // For regular elements (not image containers) apply text-align
        // Очищаємо попередні text-align класи
        className = className.replace(/\btext-(left|center|right)\b/g, '').trim();
        
        if (style.includes('text-align: center')) {
          newStyle = newStyle.replace(/text-align:\s*center;?\s*/g, '').trim();
          className = className ? `${className} text-center` : 'text-center';
        } else if (style.includes('text-align: left')) {
          newStyle = newStyle.replace(/text-align:\s*left;?\s*/g, '').trim();
          className = className ? `${className} text-left` : 'text-left';
        } else if (style.includes('text-align: right')) {
          newStyle = newStyle.replace(/text-align:\s*right;?\s*/g, '').trim();
          className = className ? `${className} text-right` : 'text-right';
        }
        

        if (element.tagName === 'IMG' && !element.closest('[data-image-group]')) {
          newStyle = newStyle.replace(/max-width:\s*100%;?\s*/g, '').replace(/width:\s*100%;?\s*/g, '').trim();
          if (!newStyle.includes('max-width:')) {
            newStyle = newStyle ? `${newStyle}; max-width: 70%;` : 'max-width: 70%;';
          }
        }
        
        if (element.tagName === 'IMG' && element.closest('[data-image-group="column"]')) {
          newStyle = newStyle.replace(/max-width:\s*100%;?\s*/g, '').replace(/width:\s*100%;?\s*/g, '').trim();
          if (!newStyle.includes('max-width:')) {
            newStyle = newStyle ? `${newStyle}; max-width: 70%;` : 'max-width: 70%;';
          }
        }
      }
      
      if (newStyle) {
        element.setAttribute('style', newStyle);
      } else {
        element.removeAttribute('style');
      }
      
      if (className) {
        element.setAttribute('class', className);
      }
    });
    
    return tempDiv.innerHTML;
  }, []);

  useImperativeHandle(ref, () => ({
    processHtmlWithTailwind,
    getContent: () => editorRef.current?.innerHTML || '',
    setContent: (html) => {
      if (editorRef.current) {
        editorRef.current.innerHTML = html;
      }
    }
  }), [processHtmlWithTailwind]);

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const emitChange = useCallback(() => {
    if (onChange && editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  }, [onChange]);

  const handleInput = useCallback(() => {
  
    if (onChange && editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  }, [onChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      document.execCommand('insertHTML', false, '&nbsp;&nbsp;&nbsp;&nbsp;');
      emitChange();
    } else if (e.key === 'Enter') {

      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const container = range.commonAncestorContainer;
        

        let imageContainer = null;
        let currentNode = container.nodeType === Node.TEXT_NODE ? container.parentNode : container;
        
        while (currentNode && currentNode !== editorRef.current) {
          if (currentNode.nodeType === Node.ELEMENT_NODE && 
              currentNode.hasAttribute && 
              currentNode.hasAttribute('data-image-group')) {
            imageContainer = currentNode;
            break;
          }
          currentNode = currentNode.parentNode;
        }
        

        if (imageContainer) {
          e.preventDefault();
          

          const newParagraph = document.createElement('p');
          newParagraph.innerHTML = '<br>';
          

          if (imageContainer.nextSibling) {
            imageContainer.parentNode.insertBefore(newParagraph, imageContainer.nextSibling);
          } else {
            imageContainer.parentNode.appendChild(newParagraph);
          }
          

          const newRange = document.createRange();
          newRange.setStart(newParagraph, 0);
          newRange.collapse(true);
          
          selection.removeAllRanges();
          selection.addRange(newRange);
          
          emitChange();
        }
      }
    }
  }, [emitChange]);

  const applyImageAlignment = useCallback((img, cmd) => {

    const parentContainer = img.closest('[data-image-group]');
    
    if (parentContainer) {
      const groupType = parentContainer.getAttribute('data-image-group');
      
      if (groupType === 'column') {

        const targetWrapper = img.closest('.image-wrapper');
        if (targetWrapper) {
          if (cmd === 'justifyCenter') {
            targetWrapper.style.justifyContent = 'center';
          } else if (cmd === 'justifyLeft') {
            targetWrapper.style.justifyContent = 'flex-start';
          } else if (cmd === 'justifyRight') {
            targetWrapper.style.justifyContent = 'flex-end';
          }
          emitChange();
        }
      } else if (groupType === 'row') {

        if (cmd === 'justifyCenter') {
          parentContainer.style.margin = '10px auto';
          parentContainer.style.justifyContent = 'center';
        } else if (cmd === 'justifyLeft') {
          parentContainer.style.margin = '10px 0 10px 0';
          parentContainer.style.justifyContent = 'flex-start';
        } else if (cmd === 'justifyRight') {
          parentContainer.style.margin = '10px 0 10px 0';
          parentContainer.style.justifyContent = 'flex-end';
        }
        emitChange();
      }
      return;
    }

    img.style.removeProperty('float');
    img.style.removeProperty('text-align');
    img.style.removeProperty('clear');
    

    if (cmd === 'justifyLeft') {
      img.style.float = 'left';
      img.style.margin = '10px 15px 10px 0';
      img.style.clear = 'left';
      img.style.maxWidth = '70%';
    } else if (cmd === 'justifyRight') {
      img.style.float = 'right';
      img.style.margin = '10px 0 10px 15px';
      img.style.clear = 'right';
      img.style.maxWidth = '70%';
    } else if (cmd === 'justifyCenter') {
      img.style.float = 'none';
      img.style.margin = '10px auto';
      img.style.display = 'block';
      img.style.maxWidth = '70%';
    }
  }, [emitChange]);


  const [lastClickedImage, setLastClickedImage] = useState(null);

  const handleImageClick = useCallback((e) => {
    if (e.target.tagName === 'IMG') {
      setLastClickedImage(e.target);
    }
  }, []);


  const exec = useCallback((cmd, arg = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();

    if (cmd === 'justifyLeft' || cmd === 'justifyCenter' || cmd === 'justifyRight') {
      const selection = window.getSelection();
      
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        

        if (range.startContainer.nodeType === Node.ELEMENT_NODE && 
            range.startContainer.tagName === 'IMG') {
          const img = range.startContainer;
          applyImageAlignment(img, cmd);
          emitChange();
          return;
        }
      
        if (lastClickedImage && lastClickedImage.parentNode) {
          applyImageAlignment(lastClickedImage, cmd);
          emitChange();
          return;
        }
        

        const container = range.commonAncestorContainer;
        let searchContainer = container;
        
        if (container.nodeType === Node.TEXT_NODE) {
          searchContainer = container.parentNode;
        }
        

        const images = searchContainer.querySelectorAll('img');
        
        if (images.length > 0) {

          if (images.length === 1) {
            applyImageAlignment(images[0], cmd);
            emitChange();
            return;
          }
          

          let closestImg = null;
          let minDistance = Infinity;
          
          for (let i = 0; i < images.length; i++) {
            const img = images[i];
            const imgRange = document.createRange();
            imgRange.selectNode(img);
            

            const distance = Math.abs(range.startOffset - imgRange.startOffset);
            
            if (distance < minDistance) {
              minDistance = distance;
              closestImg = img;
            }
          }
          
          if (closestImg) {
            applyImageAlignment(closestImg, cmd);
            emitChange();
            return;
          }
        }
      }
    }
    
    document.execCommand(cmd, false, arg);
    emitChange();
  }, [emitChange, applyImageAlignment, lastClickedImage]);

  const onInsertLink = useCallback(() => {
    const url = window.prompt(t('Inserisci URL'), 'https://');
    if (url) exec('createLink', url);
  }, [t, exec]);

  const onInsertImage = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.onchange = () => {
      const file = input.files && input.files[0];
      if (!file) return;
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert(t('File size should not exceed 5MB'));
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        if (editorRef.current) {
          editorRef.current.focus();
          

          const img = document.createElement('img');
          img.src = reader.result;
          img.style.cssText = 'width: 400px; height: auto; display: block; margin: 10px auto; object-fit: contain;';
          img.alt = 'image';
          

          const range = window.getSelection().getRangeAt(0);
          range.deleteContents();
          range.insertNode(img);
          

          range.setStartAfter(img);
          range.collapse(true);
          window.getSelection().removeAllRanges();
          window.getSelection().addRange(range);
          
          emitChange();
        }
      };
      reader.readAsDataURL(file);
    };
    input.click();
  }, [t, emitChange]);

  const wrapSelectedImagesInRow = useCallback(() => {
    if (!editorRef.current) return;
    

    const selection = window.getSelection();
    if (selection.rangeCount === 0) {
      alert(t('Seleziona prima le immagini che vuoi organizzare'));
      return;
    }
    const range = selection.getRangeAt(0);
    const selectedImages = [];
    const selectedContainers = [];
    

    const walker = document.createTreeWalker(
      range.commonAncestorContainer,
      NodeFilter.SHOW_ELEMENT,
      {
        acceptNode: (node) => {
          if (node.tagName === 'IMG') {

            if (range.intersectsNode(node)) {
              return NodeFilter.FILTER_ACCEPT;
            }
          } else if (node.hasAttribute && node.hasAttribute('data-image-group')) {

            if (range.intersectsNode(node)) {
              return NodeFilter.FILTER_ACCEPT;
            }
          }
          return NodeFilter.FILTER_SKIP;
        }
      }
    );
    
    let node;
    while ((node = walker.nextNode())) {
      if (node.tagName === 'IMG') {
        selectedImages.push(node);
      } else if (node.hasAttribute && node.hasAttribute('data-image-group')) {
        selectedContainers.push(node);
      }
    }
    
    

    if (selectedImages.length === 0 && selectedContainers.length > 0) {
      selectedContainers.forEach(container => {
        const imagesInContainer = container.querySelectorAll('img');
        selectedImages.push(...imagesInContainer);
      });
    }
    

    if (selectedImages.length === 0) {
      const allImages = editorRef.current.querySelectorAll('img');
      if (allImages.length === 0) {
        alert(t('Nessuna immagine trovata nel testo'));
        return;
      }
      selectedImages.push(...allImages);
    }
    

    const originalSelectedImages = [...selectedImages];
    

    const unpackedImages = [];
    const containersToUnpack = [];
    

    selectedImages.forEach(img => {
      const container = img.closest('[data-image-group]');
      if (container && !containersToUnpack.includes(container)) {
        containersToUnpack.push(container);
      }
    });

    if (containersToUnpack.length > 0) {
      const validContainers = containersToUnpack.filter(container => {
        const imagesInContainer = container.querySelectorAll('img');
        return Array.from(imagesInContainer).some(img => selectedImages.includes(img));
      });
      containersToUnpack.length = 0;
      containersToUnpack.push(...validContainers);
    }
    
    containersToUnpack.forEach(container => {
      const imagesInContainer = container.querySelectorAll('img');
      const insertPosition = container.parentNode;
      

      const hasSelectedImages = Array.from(imagesInContainer).some(img => selectedImages.includes(img));
      
      if (hasSelectedImages) {
        imagesInContainer.forEach(img => {

          img.removeAttribute('style');
          img.removeAttribute('width');
          img.removeAttribute('height');
          img.removeAttribute('class');

          img.className = 'max-w-[70%] h-auto block mx-auto my-2 w-auto flex-none box-border';
          

          insertPosition.insertBefore(img, container);
          unpackedImages.push(img);
        });

        container.remove();
      }
    });
    

    const finalSelectedImages = unpackedImages.length > 0 ? unpackedImages : originalSelectedImages;
    selectedImages.length = 0;
    selectedImages.push(...finalSelectedImages);
    
    
    if (selectedImages.length < 2) {
      alert(t('Seleziona almeno 2 immagini per organizzare'));
      return;
    }
    
    if (selectedImages.length > 2) {
      alert(t('Per la disposizione in riga sono consentite massimo 2 immagini'));
      return;
    }
    

    const container = document.createElement('div');
    container.setAttribute('data-image-group', 'row');
    container.style.display = 'flex';
    container.style.flexWrap = 'nowrap';
    container.style.gap = '10px';
    container.style.justifyContent = 'center';
    container.style.margin = '10px 0';
    container.style.width = '100%';
    container.style.maxWidth = '100%';
    

    const lastImage = selectedImages[selectedImages.length - 1];
    const insertPosition = lastImage.parentNode;
    const insertReference = lastImage.nextSibling;
    
    // Переносимо виділені картинки в контейнер
    selectedImages.forEach(img => {
      const wrapper = document.createElement('div');
      wrapper.className = 'image-wrapper';

      wrapper.style.flex = '0 1 50%';
      wrapper.style.boxSizing = 'border-box';
      wrapper.style.display = 'flex';
      wrapper.style.alignItems = 'center';
      wrapper.style.justifyContent = 'center';
      

      img.removeAttribute('style');
      img.removeAttribute('width');
      img.removeAttribute('height');
      img.removeAttribute('class');
      img.style.cssText = 'width: 100%; height: auto; margin: 0; max-width: 100%; display: block; flex: none; box-sizing: border-box;';
      
      wrapper.appendChild(img);
      container.appendChild(wrapper);
    });
    

    if (insertPosition && insertReference) {
      insertPosition.insertBefore(container, insertReference);
    } else if (insertPosition) {
      insertPosition.appendChild(container);
    } else {
      editorRef.current.appendChild(container);
    }
    emitChange();
  }, [t, emitChange]);

  const wrapSelectedImagesInColumn = useCallback(() => {
    if (!editorRef.current) return;
    
    const selection = window.getSelection();
    if (!selection.rangeCount) {
      alert(t('Seleziona prima le immagini da organizzare'));
      return;
    }
    
    const range = selection.getRangeAt(0);
    const selectedImages = [];
    const selectedContainers = [];
    

    const walker = document.createTreeWalker(
      range.commonAncestorContainer,
      NodeFilter.SHOW_ELEMENT,
      {
        acceptNode: (node) => {
          if (node.tagName === 'IMG') {

            if (range.intersectsNode(node)) {
              return NodeFilter.FILTER_ACCEPT;
            }
          } else if (node.hasAttribute && node.hasAttribute('data-image-group')) {

            if (range.intersectsNode(node)) {
              return NodeFilter.FILTER_ACCEPT;
            }
          }
          return NodeFilter.FILTER_SKIP;
        }
      }
    );
    
    let node;
    while ((node = walker.nextNode())) {
      if (node.tagName === 'IMG') {
        selectedImages.push(node);
      } else if (node.hasAttribute && node.hasAttribute('data-image-group')) {
        selectedContainers.push(node);
      }
    }
    
    if (selectedImages.length === 0 && selectedContainers.length > 0) {
      selectedContainers.forEach(container => {
        const imagesInContainer = container.querySelectorAll('img');
        selectedImages.push(...imagesInContainer);
      });
    }
    

    if (selectedImages.length === 0) {
      const allImages = editorRef.current.querySelectorAll('img');
      if (allImages.length === 0) {
        alert(t('Nessuna immagine trovata nel testo'));
        return;
      }
      selectedImages.push(...allImages);
    }
    

    const originalSelectedImages = [...selectedImages];
    

    const unpackedImages = [];
    const containersToUnpack = [];
    

    selectedImages.forEach(img => {
      const container = img.closest('[data-image-group]');

      if (container && !containersToUnpack.includes(container)) {
        containersToUnpack.push(container);
      }
    });
    

    if (containersToUnpack.length > 0) {
      const validContainers = containersToUnpack.filter(container => {
        const imagesInContainer = container.querySelectorAll('img');
        return Array.from(imagesInContainer).some(img => selectedImages.includes(img));
      });
      containersToUnpack.length = 0;
      containersToUnpack.push(...validContainers);
    }
    
    containersToUnpack.forEach(container => {
      const imagesInContainer = container.querySelectorAll('img');
      const insertPosition = container.parentNode;
      
      const hasSelectedImages = Array.from(imagesInContainer).some(img => selectedImages.includes(img));
      
      if (hasSelectedImages) {
        imagesInContainer.forEach(img => {

          img.removeAttribute('style');
          img.removeAttribute('width');
          img.removeAttribute('height');
          img.removeAttribute('class');

          img.className = 'max-w-[70%] h-auto block mx-auto my-2 w-auto flex-none box-border';
          

          insertPosition.insertBefore(img, container);
          unpackedImages.push(img);
        });

        container.remove();
      }
    });
    

    const finalSelectedImages = unpackedImages.length > 0 ? unpackedImages : originalSelectedImages;
    
    

    selectedImages.length = 0;
    selectedImages.push(...finalSelectedImages);
    
    if (selectedImages.length < 2) {
      alert(t('Seleziona almeno 2 immagini per organizzare'));
      return;
    }
    

    const container = document.createElement('div');
    container.setAttribute('data-image-group', 'column');
    container.style.display = 'grid';
    container.style.gridTemplateColumns = '1fr';
    container.style.gap = '10px';
    container.style.margin = '10px 0';
    container.style.width = '100%';
    container.style.maxWidth = '100%';
    

    const lastImage = selectedImages[selectedImages.length - 1];
    const insertPosition = lastImage.parentNode;
    const insertReference = lastImage.nextSibling;
    

    selectedImages.forEach(img => {
      const wrapper = document.createElement('div');
      wrapper.className = 'image-wrapper';
      wrapper.style.width = '100%';
      wrapper.style.display = 'flex';
      wrapper.style.alignItems = 'center';
      wrapper.style.justifyContent = 'center';
      

      img.removeAttribute('style');
      img.removeAttribute('width');
      img.removeAttribute('height');
      img.removeAttribute('class');
      img.style.cssText = 'max-width: 70%; height: auto; margin: 0; display: block; flex: none; box-sizing: border-box;';
      
      wrapper.appendChild(img);
      container.appendChild(wrapper);
    });
    

    if (insertPosition && insertReference) {
      insertPosition.insertBefore(container, insertReference);
    } else if (insertPosition) {
      insertPosition.appendChild(container);
    } else {
      editorRef.current.appendChild(container);
    }
    emitChange();
  }, [t, emitChange]);


  return (
    <div className='simple-editor'>
      {showToolbar && (
        <div className='simple-editor-toolbar'>
          <button type='button' className='toolbar-button' onClick={() => exec('bold')}><b>B</b></button>
          <button type='button' className='toolbar-button' onClick={() => exec('italic')}><i>I</i></button>
          <button type='button' className='toolbar-button' onClick={() => exec('underline')}><u>U</u></button>
          <div className='toolbar-separator' />
          <button type='button' className='toolbar-button' onClick={() => exec('formatBlock', 'H1')}>H1</button>
          <button type='button' className='toolbar-button' onClick={() => exec('formatBlock', 'H2')}>H2</button>
          <button type='button' className='toolbar-button' onClick={() => exec('formatBlock', 'P')}>P</button>
          <div className='toolbar-separator' />
          <button type='button' className='toolbar-button' onClick={() => exec('insertUnorderedList')}>• List</button>
          <button type='button' className='toolbar-button' onClick={() => exec('insertOrderedList')}>1. List</button>
          <div className='toolbar-separator' />
          <button type='button' className='toolbar-button' onClick={() => exec('justifyLeft')}>⟸</button>
          <button type='button' className='toolbar-button' onClick={() => exec('justifyCenter')}>⇔</button>
          <button type='button' className='toolbar-button' onClick={() => exec('justifyRight')}>⟹</button>
          <div className='toolbar-separator' />
          <button type='button' className='toolbar-button' onClick={onInsertLink}>Link</button>
          <button type='button' className='toolbar-button' onClick={onInsertImage} title={t('Carica immagine dal computer (max 5MB)')}>{t('Immagine')}</button>
          <div className='toolbar-separator' />
          <button type='button' className='toolbar-button' onClick={wrapSelectedImagesInRow} title={t('Organizza le immagini in riga (2 per riga)')}>📷📷</button>
          <button type='button' className='toolbar-button' onClick={wrapSelectedImagesInColumn} title={t('Organizza le immagini in colonna (una sotto l\'altra)')}>📷</button>
          <div className='toolbar-separator' />
          <button type='button' className='toolbar-button' onClick={() => exec('removeFormat')}>{t('Rimuovi')}</button>
        </div>
      )}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onClick={handleImageClick}
        placeholder={placeholder}
        className='simple-editor-content'
        style={{
          minHeight: `${rows * 24}px`,
        }}
        {...props}
      />
    </div>
  );
});

export default SimpleEditor;


